import { FC, ReactNode, useCallback, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import styles from './PunchupPageShell.module.css';
import { IdParamsType } from 'utils/types';
import { isEmbeddedInIframe } from 'utils/utils';
import { MainLayout } from 'layouts/MainLayout';
import { ROUTES } from 'features/routes';

interface PunchupPageShellProps {
	children: ReactNode;
	pageName: string;
	/**
	 * When provided, the downstroke breadcrumb will show this as an intermediate
	 * crumb between the show title and `pageName`. Use when a sub-page is nested
	 * inside another sub-page (e.g. Send Tickets is inside Comped Tickets).
	 */
	parentPageName?: string;
	/**
	 * The tixologi route for the parent sub-page (e.g. `/comped-tickets/${id}`).
	 * When provided, clicking the intermediate breadcrumb crumb in downstroke
	 * navigates the iframe to this route instead of back to the show dashboard.
	 */
	parentRoute?: string;
	onBack?: () => void;
}

export const PunchupPageShell: FC<PunchupPageShellProps> = ({
	children,
	pageName,
	parentPageName,
	parentRoute,
	onBack,
}) => {
	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();

	const handleBack = useCallback(() => {
		if (onBack) {
			onBack();
		} else {
			push(generatePath(ROUTES.DASHBOARD_PATH, { id }));
		}
	}, [onBack, push, id]);

	useEffect(() => {
		// page-ready cancels the host's debounced loading cover so it doesn't flash between pages.
		window.parent.postMessage({ action: 'page-ready' }, '*');
		window.parent.postMessage({ action: 'sub-page-enter', pageName, parentPageName, parentRoute }, '*');
		window.parent.postMessage({ action: 'scroll-to-top' }, '*');
		return () => {
			// page-leave triggers the host's debounced loading cover for the transition gap.
			window.parent.postMessage({ action: 'page-leave' }, '*');
			window.parent.postMessage({ action: 'sub-page-exit' }, '*');
		};
	}, [pageName, parentPageName, parentRoute]);

	useEffect(() => {
		const handleParentMessage = (event: MessageEvent) => {
			if (!isEmbeddedInIframe()) return;
			if (event.source !== window.parent) return;
			if (event.data?.action === 'navigate-to-dashboard') {
				handleBack();
			} else if (event.data?.action === 'navigate-to-sub-page') {
				// postMessage payloads are untrusted — narrow the path before handing it to react-router.
				const path = event.data?.path;
				if (typeof path === 'string' && path.startsWith('/')) push(path);
			}
		};

		window.addEventListener('message', handleParentMessage);
		return () => window.removeEventListener('message', handleParentMessage);
	}, [handleBack, push]);

	// In an iframe (Punchup) the breadcrumb owns back navigation — no button needed.
	// Standalone tixologist has no breadcrumb, so we render one here.
	const isEmbedded = isEmbeddedInIframe();

	return (
		<MainLayout>
			{!isEmbedded && (
				<button className={styles.backButton} onClick={handleBack}>
					← Back
				</button>
			)}
			{children}
		</MainLayout>
	);
};
